import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DataTableDirective } from 'angular-datatables';
import { catchError, debounceTime, distinctUntilChanged, finalize, map, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { helper } from 'src/environments/helper';
import { PermissionsService } from 'src/app/services/permissions.service';
import { FormControl, FormGroup } from '@angular/forms';
import { MatCalendar } from '@angular/material/datepicker';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import { HttpClient } from '@angular/common/http';
import { AwbService } from './awb.service';
import { environment } from 'src/environments/environment';
import Swal from 'sweetalert2';
import { DataTablesResponse } from 'src/app/interfaces/data-table-response.interface';
import { AppFilesService } from 'src/app/services/app-files.service';
import { NotificationService } from 'src/app/services/notification.service';
import { Generic } from 'src/app/interfaces/generic.interface';
import { UserOrganigramaSearchPayload } from 'src/app/interfaces/common.interface';
import { CommonService } from 'src/app/services/common.service';
import { EntitatiService } from '../entitati/entitati.service';

@Component({
    selector: 'app-awb',
    templateUrl: './awb.component.html',
    styleUrls: ['./awb.component.scss']
})
export class AwbComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild(DataTableDirective, { static: false })
    dtElement!: DataTableDirective;
    @ViewChild(PopoverDirective) popover!: PopoverDirective;
    helper = helper;

    errorTitle: string = environment.config.customNotifications.headers.error;
    errorIcon: string = environment.config.customNotifications.icons.error;
    errorType: string = environment.config.customNotifications.icons.error;

    isLoading = false;
    showCard = false;

    // Popover dates

    @ViewChild('startCalendar') startCalendar!: MatCalendar<Date>;
    @ViewChild('endCalendar') endCalendar!: MatCalendar<Date>;

    today: Date = new Date();
    tomorrow: Date = new Date();
    selectedStartDate: Date = new Date(new Date().getFullYear(), 0, 1);
    private _selectedEndDate: Date = new Date();
    selectedFilteringButton: string = 'current_year';

    setLastXDays(numberOfDays: number): void {
        const date = new Date();
        date.setDate(this.today.getDate() - numberOfDays);
        this.selectedStartDate = date;
        this.updateCalendarView(this.startCalendar, date);

        this.selectedEndDate = new Date(this.today);
        this.updateCalendarView(this.endCalendar, this.selectedEndDate);

        this.rerenderDT();
        this.selectedFilteringButton = `last_${numberOfDays}_days`;
    }

    setYear(yearsBack: number): void {
        const year = this.today.getFullYear() - yearsBack;
        this.selectedStartDate = new Date(year, 0, 1); // 1 Ianuarie
        this.updateCalendarView(this.startCalendar, this.selectedStartDate);
        this.selectedEndDate = yearsBack === 0 ? this.today : new Date(year, 11, 31);
        this.updateCalendarView(this.endCalendar, this.selectedEndDate);
        this.rerenderDT();
        this.selectedFilteringButton =
            yearsBack === 0 ? 'current_year' : 'previous_year';
    }

    updateCalendarView(calendar: MatCalendar<Date>, date: Date): void {
        if (calendar) {
            calendar.activeDate = date;
        }
    }

    // Methods used to dinamically select the start date according to the end date

    get selectedEndDate(): Date {
        return this._selectedEndDate;
    }

    set selectedEndDate(date: Date) {
        this._selectedEndDate = date;
        if (this.selectedStartDate > date) {
            this.selectedStartDate = new Date(date);
        }
    }

    closePopover(): void {
        if (this.popover) {
            this.popover.hide();
        }
    }

    dtOptions: any = {};
    dtTrigger: Subject<any> = new Subject<any>();
    dtInstance!: DataTables.Api;
    dtParameters: any = {};

    statusParameters: { key: string; value: number }[] = [
        { key: '-- Alegeți --', value: -1 },
        { key: 'Procesat', value: 1 },
        { key: 'Dezactivat', value: 0 },
    ];

    dgaspc_id = environment.interop.dgaspcId;

    directiiActive: any[] = [];
    compartimenteActive!: Generic[];
    usersActive!: Generic[];
    destinatari!: any[];

    private destroy$ = new Subject<void>();

    // Filtered fields for search
    filteredDirectii: any[] = [];
    directiiControl = new FormControl();

    filteredCompartimente: any[] = [];
    compartimenteControl = new FormControl();

    filteredUsers: any[] = [];
    usersControl = new FormControl();

    destinatariControl = new FormControl();
    isDestinatariLoading = false;
    showDestinatariMessage = true;
    destinatariLoadComplete = false;

    searchParamsForm!: FormGroup;

    initialFormValues = {
        awb: '',
        borderou: '',
        document: '',
        destinatar: '',
        status: -1,
        id_directie: -1,
        id_compartiment: -1,
        user: -1,
    };

    buildSearchParamsForm() {
        this.searchParamsForm = new FormGroup({
            awb: new FormControl(''),
            borderou: new FormControl(''),
            document: new FormControl(''),
            destinatar: new FormControl(''),
            status: new FormControl(-1),
            id_directie: new FormControl(this.dgaspc_id),
            id_compartiment: new FormControl(-1),
            user: new FormControl(-1),
        });
    }

    resetForm() {
        this.searchParamsForm.reset(this.initialFormValues);

        this.filteredDirectii = this.directiiActive;
        this.directiiControl.setValue(null);

        this.filteredCompartimente = this.compartimenteActive;
        this.compartimenteControl.setValue(null);

        this.loadUsers();
        this.usersControl.setValue(null);

        this.destinatariControl.setValue(null);
        this.destinatari = [];
        this.showDestinatariMessage = true;
        this.destinatariLoadComplete = false;
        this.isDestinatariLoading = false;

        this.rerenderDT();
        this.showCard = true;
    }

    applyFilters(): void {
        this.rerenderDT(true);
    }

    toggleCard() {
        this.showCard = !this.showCard;
    }

    dtLink = environment.interop.basePath + environment.interop.indoc.awb.getAwbDt;

    constructor(
        private http: HttpClient,
        private awbService: AwbService,
        public permissionsService: PermissionsService,
        private appFilesService: AppFilesService,
        private notificationService: NotificationService,
        private commonService: CommonService,
        private entitatiService: EntitatiService,
    ) { }

    ngOnInit(): void {
        this.renderDT();
        this.buildSearchParamsForm();

        this.getActiveDirectii();
        this.loadCompartimenteActive(this.dgaspc_id);
        this.renderFieldsSearch();
    }

    ngOnDestroy(): void {
        this.dtTrigger.unsubscribe();

        this.destroy$.next();
        this.destroy$.complete();
    }

    ngAfterViewInit(): void {
        this.dtTrigger.next('');
    }

    renderFieldsSearch() {
        this.directiiControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredDirectii = this.directiiActive;
                    return of(this.directiiActive);
                }
                return this.filterDirectii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredDirectii = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.compartimenteControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredCompartimente = this.compartimenteActive;
                    return of(this.compartimenteActive);
                }
                return this.filterCompartimente(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredCompartimente = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.usersControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredUsers = this.usersActive;
                    return of(this.usersActive);
                }
                return this.filterUsers(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredUsers = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.destinatariControl.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                debounceTime(600)
            )
            .subscribe(destinatar => {
                this.fetchDestinatari(destinatar);
                this.showDestinatariMessage = true;
                this.destinatariLoadComplete = false;
            });
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterDirectii(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.directiiActive.filter(directie =>
            this.normalizeString(directie.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    getActiveDirectii() {
        this.isLoading = true;
        this.commonService.getDirectiiActive()
            .pipe(
                finalize(() => (this.isLoading = false)),
            )
            .subscribe({
                next: (res: any) => {
                    this.directiiActive = res;
                    this.filteredDirectii = [...this.directiiActive];
                    return;
                },
                error: (err: any) => {
                    console.log(err.error);
                }
            })
    }

    onDirectieSelectClosed() {
        const selectedValue = this.directiiControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredDirectii.some(directie =>
                directie.id === this.searchParamsForm.get('id_directie')?.value
            );

            if (!isSelectedInFilteredList) {
                this.directiiControl.setValue(null);
                this.filteredDirectii = this.directiiActive;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onDirectieChange(directieId: number): void {
        this.searchParamsForm.controls['id_compartiment'].patchValue(-1);
        this.searchParamsForm.controls['user'].patchValue(-1);

        if (directieId == -1) {
            this.compartimenteActive = [];
            this.filteredCompartimente = [];
            this.usersActive = [];
            this.filteredUsers = [];
            return;
        }

        if (directieId) {
            this.loadCompartimenteActive(directieId);
            this.loadUsers();
        }
    }

    loadCompartimenteActive(directieId?: number) {
        this.isLoading = true;
        let compartimenteObservable$ = directieId
            ? this.commonService.getCompartimenteByDirectieId(directieId)
            : this.commonService.getCompartimenteActive();

        compartimenteObservable$.pipe(
            map((results: any) => [...results]),
            finalize(() => (this.isLoading = false))
        ).subscribe({
            next: (res) => {
                this.compartimenteActive = [...res];
                this.filteredCompartimente = [...res];
            },
            error: (err) => {
                console.log(err);
                this.filteredCompartimente = [];
            },
        })
    }

    filterCompartimente(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.compartimenteActive.filter(compartiment =>
            this.normalizeString(compartiment.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onCompartimentSelectClosed() {
        const selectedValue = this.compartimenteControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredCompartimente.some(compartiment =>
                compartiment.id === this.searchParamsForm.get('id_compartiment')?.value
            );

            if (!isSelectedInFilteredList) {
                this.compartimenteControl.setValue(null);
                this.filteredCompartimente = this.compartimenteActive;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onCompartimentChange(compartimentId: number) {
        this.searchParamsForm.controls['user'].patchValue(-1);

        if (compartimentId) {
            this.loadUsers();
        }
    }

    loadUsers() {
        this.isLoading = true;

        const payload: UserOrganigramaSearchPayload = {
            directie_id: this.searchParamsForm.get('id_directie')?.value,
            compartiment_id: this.searchParamsForm.get('id_compartiment')?.value !== -1 ? this.searchParamsForm.get('id_compartiment')?.value : null,
        };

        this.commonService
            .getUsersByOrganigrama(payload)
            .pipe(
                map((results: any) => [...results]),
                finalize(() => (this.isLoading = false))
            ).subscribe({
                next: (res) => {
                    this.usersActive = [...res];
                    this.filteredUsers = [...res];
                },
                error: (err) => {
                    console.log(err);
                    this.filteredUsers = [];
                },
            });
    }

    filterUsers(searchTerm: string): Observable<Generic[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        return of(this.usersActive.filter(user =>
            this.normalizeString(user.name).includes(normalizedSearchTerm)
        ));
    }

    onUsersSelectClosed() {
        const selectedValue = this.usersControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredUsers.some(user =>
                user.id === this.searchParamsForm.get('user')?.value
            );

            if (!isSelectedInFilteredList) {
                this.usersControl.setValue(null);
                this.filteredUsers = this.usersActive;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    formatDateRender(date: Date) {
        let d = new Date(date),
            month = '' + (d.getMonth() + 1),
            day = '' + d.getDate(),
            year = d.getFullYear();

        if (month.length < 2) month = '0' + month;
        if (day.length < 2) day = '0' + day;

        return [year, month, day].join('-');
    }

    renderDT() {
        this.isLoading = true;

        this.dtOptions = {
            order: [[0, 'desc']],
            serverSide: true,
            searching: false,
            responsive: true,
            pageLength: 25,
            ajax: (dataTablesParameters: any, callback: any) => {
                this.isLoading = true;

                const { id_directie, id_compartiment, ...filteredParams } = this.searchParamsForm.value;

                Object.assign(dataTablesParameters, filteredParams);

                dataTablesParameters.date_from = this.formatDateRender(this.selectedStartDate);
                dataTablesParameters.date_to = this.formatDateRender(this.selectedEndDate);

                this.dtParameters = dataTablesParameters;

                this.http
                    .post<DataTablesResponse>(this.dtLink, dataTablesParameters)
                    .subscribe((resp: any) => {
                        this.isLoading = false;

                        callback({
                            recordsTotal: resp.data.recordsTotal,
                            recordsFiltered: resp.data.recordsFiltered,
                            data: resp.data.data,
                        });
                    });
            },
            language: environment.config.romanianLanguageDataTable,
            columns: [
                {
                    title: 'ID',
                    data: 'id',
                },
                {
                    title: 'AWB',
                    data: 'identificator_data',
                },
                {
                    title: 'Documente',
                    data: 'conexe_html',
                },
                {
                    title: 'Operator',
                    data: 'denumire_user',
                    orderable: false,
                },
                {
                    title: 'Status',
                    data: 'status_awb',
                    orderable: false,
                },
                {
                    title: 'Destinatar',
                    data: 'denumire_entity',
                },
                {
                    title: 'Borderou',
                    data: 'id_borderou',
                },
                {
                    title: 'Creat la',
                    data: 'created_at',
                    render: (data: any) => {
                        return this.formatDate(data);
                    }
                },
                {
                    title: 'Acțiuni',
                    data: null,
                    className: 'all',
                    orderable: false,
                    render: () => {
                        return `
			                <div class="d-flex align-center gap-1">
                                <button class="action-btn delete-intern delete" title="Ștergere">
                                    <img class="btn-img" src="../../../../assets/icons/delete.svg" alt="delete"/>
                                </button>
                                <button class="action-btn download" style="font-size: 11px; color: #fff" title="Descarcă AWB">
                                    <i class="btn-img fa fa-download"></i>
                                </button>
                                <button class="action-btn delete-awb-posta delete" title="Ștergere AWB Poștă">
                                    <div class="d-flex align-items-center">
                                        <img class="btn-img" src="../../../../assets/icons/delete.svg" alt="delete"/>
                                        <div style="margin-top: 2px; font-size: 12px; line-height: 1; color: #fff">AWB</div>
                                    </div>
                                </button>
                            </div>
						`;
                    },
                },
            ],
            rowCallback: (row: Node, data: any[] | any) => {
                $('td', row).off('click');

                const lastTd = (row as HTMLElement).querySelector('td:last-child');
                const downloadButton = lastTd?.querySelector('.download') as HTMLElement;
                const deleteIcon = lastTd?.querySelector('.delete-intern') as HTMLElement;
                const deleteAwbPostaIcon = lastTd?.querySelector('.delete-awb-posta') as HTMLElement;

                if (downloadButton) {
                    if (
                        !this.permissionsService.hasPermission(
                            'indoc-module.settings.posta-awb.download-pdf'
                        ) || !data.active
                    ) {
                        downloadButton.style.display = 'none';
                    }
                    downloadButton.addEventListener('click', () => {
                        const { id } = data;
                        this.downloadAWB(id);
                    });
                }

                if (deleteIcon) {
                    if (
                        !this.permissionsService.hasPermission(
                            'indoc-module.settings.posta-awb.delete-intern'
                        ) || !data.active
                    ) {
                        deleteIcon.style.display = 'none';
                    }
                    deleteIcon.addEventListener('click', () => {
                        const { id } = data;
                        this.deleteEntry(id);
                    });
                }

                if (deleteAwbPostaIcon) {
                    if (
                        !this.permissionsService.hasPermission(
                            'indoc-module.settings.posta-awb.delete'
                        ) || !data.active
                    ) {
                        deleteAwbPostaIcon.style.display = 'none';
                    }
                    deleteAwbPostaIcon.addEventListener('click', () => {
                        const { id } = data;
                        this.deleteAwbPosta(id);
                    });
                }

                return row;
            },
        };
    }

    downloadAWB(id: number) {
        this.isLoading = true;
        let fullPath = this.awbService.downloadPDFAwbUrl + id;

        this.appFilesService.downloadFile(fullPath)
            .then(() => {
                this.isLoading = false;
            })
            .catch(err => {
                console.error('Print error: ', err);
                this.isLoading = false;
            })
    }

    deleteEntry(id: number) {
        Swal.fire({
            title: helper.swalTextConfig.commonTitles.areYouSure,
            text: helper.swalTextConfig.generalMessages.delete,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: helper.swalTextConfig.buttonLabels.confirm,
            cancelButtonText: helper.swalTextConfig.buttonLabels.close,
            showLoaderOnConfirm: true,
            reverseButtons: true,
        }).then((result) => {
            if (result.isConfirmed) {
                this.awbService.deleteAwb(id).subscribe({
                    next: () => {
                        this.rerenderDT();
                        Swal.fire({
                            title: this.helper.swalTextConfig.headers.deleted,
                            text: 'Înregistrare ștearsă cu succes.',
                            icon: 'success',
                            confirmButtonText: helper.swalTextConfig.buttonLabels.ok,
                        });
                    },
                    error: (err: any) => {
                        Swal.fire({
                            title: helper.dialogConfig.headers.errorAtentie,
                            text: err.error.errors.message[0],
                            icon: 'error',
                            confirmButtonText: helper.dialogConfig.buttonLabels.close,
                        })
                    }
                });
            }
        });
    }

    deleteAwbPosta(id: number) {
        Swal.fire({
            title: helper.swalTextConfig.commonTitles.areYouSure,
            text: helper.swalTextConfig.generalMessages.delete,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: helper.swalTextConfig.buttonLabels.confirm,
            cancelButtonText: helper.swalTextConfig.buttonLabels.close,
            showLoaderOnConfirm: true,
            reverseButtons: true,
        }).then((result) => {
            if (result.isConfirmed) {
                this.awbService.deleteAwbPosta(id).subscribe({
                    next: () => {
                        this.rerenderDT();
                        Swal.fire({
                            title: this.helper.swalTextConfig.headers.deleted,
                            text: 'Înregistrare ștearsă cu succes.',
                            icon: 'success',
                            confirmButtonText: helper.swalTextConfig.buttonLabels.ok,
                        });
                    },
                    error: (err: any) => {
                        Swal.fire({
                            title: helper.dialogConfig.headers.errorAtentie,
                            text: err.error.errors.message[0],
                            icon: 'error',
                            confirmButtonText: helper.dialogConfig.buttonLabels.close,
                        })
                    }
                });
            }
        });
    }

    rerenderDT(paginate: boolean = false): void {
        this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
            dtInstance.draw(paginate);
        });

        this.showCard = false;
    }

    formatDate(input: string): string {
        const date = new Date(input);

        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();

        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const seconds = date.getSeconds().toString().padStart(2, '0');

        return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
    }

    downloadCSV() {
        this.isLoading = true;

        let fullPath = this.awbService.downloadCSVUrl;

        const { id_directie, id_compartiment, ...filteredParams } = this.searchParamsForm.value;

        const payload = {
            ...filteredParams,
            date_from: this.formatDateRender(this.selectedStartDate),
            date_end: this.formatDateRender(this.selectedEndDate)
        }

        this.appFilesService.postDownloadFile(fullPath, payload)
            .then(() => {
                this.isLoading = false;
            })
            .catch(err => {
                console.error('Download csv error: ', err);
                this.isLoading = false;
            })
    }

    downloadPDF() {
        this.isLoading = true;

        let fullPath = this.awbService.downloadPDFUrl;

        const { id_directie, id_compartiment, ...filteredParams } = this.searchParamsForm.value;

        const payload = {
            ...filteredParams,
            date_from: this.formatDateRender(this.selectedStartDate),
            date_end: this.formatDateRender(this.selectedEndDate)
        }

        this.appFilesService.postDownloadFile(fullPath, payload)
            .then(() => {
                this.isLoading = false;
            })
            .catch(err => {
                console.error('Download pdf error: ', err);
                this.isLoading = false;
            })
    }

    fetchDestinatari(destinatar: string) {
        if (destinatar && destinatar.length >= 2) {
            this.isDestinatariLoading = true;
            let payload = { search: destinatar };
            this.entitatiService.searchEntitatiDT(payload).subscribe({
                next: (response: any) => {
                    this.destinatari = response.data;
                    this.isDestinatariLoading = false;
                    this.destinatariLoadComplete = true;
                    this.showDestinatariMessage = this.destinatari.length === 0;
                },
                error: (err: any) => {
                    this.isDestinatariLoading = false;
                    this.showDestinatariMessage = true;
                    this.destinatariLoadComplete = true;
                    console.log(err);
                }
            });
        } else {
            this.destinatari = [];
            this.showDestinatariMessage = true;
            this.destinatariLoadComplete = false;
        }
    }
}
